import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { t } from 'i18next';
import { useState } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';
import * as yup from 'yup';

import { emailSchema } from '~schemas/emailSchema';

import Label from './Label';

type Props<T extends FieldValues> = UseControllerProps<T> & {
  isDisabled?: boolean;
  label?: React.ReactNode;
  placeholder?: string;
  sx?: SxProps<Theme>;
};

export const createRecipientsSchema = (label: string) =>
  yup
    .array()
    .of(yup.string().defined())
    .min(1, () => t('form_validation_errors.min_count', { count: 1, field: label }))
    .defined();

const splitIntoEmailAddresses = (value: string) => value.split(/[\s,;]+/).filter(Boolean);

export default function EmailRecipientsInput<T extends FieldValues>({
  control,
  isDisabled,
  label,
  name,
  placeholder,
  rules,
  sx,
}: Props<T>) {
  const { field, fieldState } = useController({ control, name, rules });
  const [rejection, setRejection] = useState<string>();
  const recipients: string[] = field.value ?? [];
  const error = rejection ?? fieldState.error?.message ?? fieldState.error?.root?.message;

  const handleChange = (_event: React.SyntheticEvent, values: string[]) => {
    const accepted: string[] = [];
    let rejectionMessage: string | undefined;

    // A pasted or typed list ("a@x.io, b@x.io") becomes one chip per address.
    values.flatMap(splitIntoEmailAddresses).forEach((email) => {
      const isDuplicate = accepted.some(
        (acceptedEmail) => acceptedEmail.toLowerCase() === email.toLowerCase(),
      );

      if (isDuplicate) {
        return;
      }

      if (!emailSchema.isValidSync(email)) {
        rejectionMessage = t('form_fields.email_recipients.invalid_email', { email });

        return;
      }

      accepted.push(email);
    });

    setRejection(rejectionMessage);
    field.onChange(accepted);
  };

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      fullWidth
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label>{label}</Label>}

      <Autocomplete<string, true, false, true>
        autoSelect
        disabled={isDisabled}
        freeSolo
        multiple
        onChange={handleChange}
        options={[]}
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(error)}
            placeholder={recipients.length ? undefined : placeholder}
            slotProps={{
              htmlInput: { ...params.inputProps, 'data-ui': 'email-recipients-input' },
            }}
          />
        )}
        renderValue={(values, getItemProps) =>
          values.map((email, index) => {
            const { key, ...tagProps } = getItemProps({ index });

            return <Chip key={key} label={email} size="small" {...tagProps} />;
          })
        }
        value={recipients}
      />

      {error && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
